import { Dexie } from 'dexie';
import { DB } from '../../../typings/model/db';
import { IMsg } from '../../../typings/model/im';

const DB_NAME = 'KKDatabase';
const MESSAGE_MAX_COUNT = 20;

export type DBResultData<T> = T | null;

export interface DBResult<T> {
  error?: unknown;
  data: DBResultData<T>;
}

/**
 * 是否能使用 DB，装饰器
 */
function isActive() {
  return function (target: DBContainerManager, methodName: string, descriptor: PropertyDescriptor) {
    const oldValue = descriptor.value;
    // eslint-disable-next-line no-param-reassign
    descriptor.value = async function (...args: unknown[]) {
      try {
        const isDBReady = target.getDBStatus.call(this);
        if (!isDBReady) {
          return {
            data: null,
          };
        }
        const result = await oldValue.apply(this, args);
        return result;
      } catch (e) {
        target.setDBStatus.call(this, false);
        return {
          error: e,
          data: null,
        };
      }
    };
    return descriptor;
  };
}

export default class DBContainerManager {
  private static instance: DBContainerManager;

  public static getInstance(): DBContainerManager | null {
    if (!self.indexedDB) {
      return null;
    }

    if (!DBContainerManager.instance) {
      DBContainerManager.instance = new DBContainerManager();
    }
    return DBContainerManager.instance;
  }

  private db: DB.IDexie;
  private isDBReady: boolean;

  constructor() {
    this.db = new Dexie(DB_NAME);
    this.initDB();
    this.deleteOldDB();
    this.isDBReady = true;
  }

  initDB() {
    this.db.version(5).stores({
      /** 用户信息 */
      userInfo: '&uid',
      /** 链接解析 */
      linkMsgInfo: '&link',
      /** 历史消息 */
      messageList: '&[channelMsg.channelId+origin.sequence], channelMsg.channelId, origin.sequence',
    });
  }

  deleteOldDB() {
    const oldDBName = 'FriendDatabase';
    Dexie.exists(oldDBName).then((isExist) => {
      if (isExist) {
        const db = new Dexie(oldDBName);
        db?.delete();
      }
    });
  }

  getDBStatus() {
    return this.isDBReady;
  }

  setDBStatus(flag: boolean) {
    this.isDBReady = flag;
  }

  /**
   * 批量存入用户简要信息
   * - 用于聊天场景
   * @param userInfoList 用户简要信息列表
   * @returns 是否存储成功
   */
  @isActive()
  async bulkPutUserInfo(userInfoList: DB.IUserInfo[]): Promise<DBResult<boolean>> {
    if (!this.db.userInfo) {
      return {
        data: false,
      };
    }

    await this.db.userInfo.bulkPut(userInfoList);
    return {
      data: true,
    };
  }

  /**
   * 查询用户简要信息数据
   * - 用于聊天场景
   * @param userIdList 用户id列表
   * @returns 用户简要信息列表
   */
  @isActive()
  async getUserInfoList(userIdList: string[]): Promise<DBResult<DB.IUserInfo[]>> {
    const table = this.db.userInfo;
    if (!table || !userIdList.length) {
      return {
        data: [],
      };
    }

    const dataList = await this.db.transaction('r', table, () => {
      const promiseList = userIdList.map(uid => table
        .where('uid')
        .equals(uid)
        .toArray());
      return Promise.all(promiseList);
    });
    const result = dataList.reduce((totalList, list) => totalList.concat(list), []);
    return {
      data: result,
    };
  }

  /**
   * 批量存入链接解析的数据，用于聊天场景使用
   * @param dataList 链接解析的内容集合
   * @returns 是否存入成功
   */
  @isActive()
  async bulkPutLinkMsgInfo(dataList: DB.ILinkMsgInfo[]): Promise<DBResult<boolean>> {
    if (!this.db.linkMsgInfo) {
      return {
        data: false,
      };
    }

    await this.db.linkMsgInfo.bulkPut(dataList);
    return {
      data: true,
    };
  }

  /**
   * 查询获取链接解析的数据
   * @param linkList 查询的链接集合
   * @returns 链接解析的内容集合
   */
  @isActive()
  async getLinkMsgList(linkList: string[]): Promise<DBResult<DB.ILinkMsgInfo[]>> {
    const table = this.db.linkMsgInfo;
    if (!table || !linkList.length) {
      return {
        data: [],
      };
    }

    const dataList = await this.db.transaction('r', table, () => {
      const promiseList = linkList.map(link => table
        .where('link')
        .equals(link)
        .toArray());
      return Promise.all(promiseList);
    });
    const result = dataList.reduce((totalList, list) => totalList.concat(list), []);
    return {
      data: result,
    };
  }

  @isActive()
  async bulkPutMessageList(channelId: string, dataList: Array<IMsg>): Promise<DBResult<boolean>> {
    const table = this.db.messageList;
    if (!table) {
      return {
        data: false,
      };
    }

    const isValidData = dataList.every(item => item.channelMsg?.channelId && item.origin?.sequence);
    if (!isValidData) {
      return {
        data: false,
      };
    }

    await this.db.transaction('rw', table, async () => {
      if (dataList.length >= MESSAGE_MAX_COUNT) {
        await table
          .where('channelMsg.channelId')
          .equals(channelId)
          .delete();
      } else {
        const count = await table
          .where('channelMsg.channelId')
          .equals(channelId)
          .count();
        if (count > MESSAGE_MAX_COUNT) {
          // 假定 put 进来的都是重复数据，那么保底留 MESSAGE_MAX_COUNT 条数据
          await table.where('channelMsg.channelId')
            .equals(channelId)
            .limit(count - MESSAGE_MAX_COUNT)
            .delete();
        }
      }
      await table.bulkPut(dataList);
    });
    return {
      data: true,
    };
  }

  @isActive()
  async getMessageList(channelId: string): Promise<DBResult<Array<IMsg>>> {
    const table = this.db.messageList;
    if (!table) {
      return {
        data: [],
      };
    }

    const data = await table
      .where('channelMsg.channelId')
      .equals(channelId)
      .limit(MESSAGE_MAX_COUNT)
      .reverse()
      .sortBy('origin.sequence');
    return {
      data: data.reverse(),
    };
  }

  @isActive()
  async getLastMessage(channelIdList: Array<string>): Promise<DBResult<Record<string, IMsg>>> {
    const table = this.db.messageList;
    if (!table) {
      return {
        data: {},
      };
    }
    const promiseList = channelIdList.map(channelId => table
      .where('channelMsg.channelId')
      .equals(channelId)
      .reverse()
      .limit(1)
      .sortBy('origin.sequence'));
    const data = await Promise.all(promiseList);
    const result = data.reduce((ret: Record<string, IMsg>, item, index) => {
      if (item?.length) {
        // eslint-disable-next-line no-param-reassign,prefer-destructuring
        ret[channelIdList[index]] = item[0];
      }
      return ret;
    }, {});
    return {
      data: result,
    };
  }

  restart(): Promise<DBResult<boolean>> {
    this.isDBReady = false;
    return this.db.delete().then(() => {
      this.db = new Dexie(DB_NAME);
      this.initDB();
      this.isDBReady = true;
      return {
        data: true,
      };
    });
  }
}
